import { BusinessDashboardUser } from "./types";
import { isExpired, isMembershipCancelled } from "./utils";

export type Member = BusinessDashboardUser;

// Same tiering as the payout audit: premium, premium_quarterly and
// premium_yearly are upgrades; every other plan is a trial.
export const isTrialProduct = (name?: string) => !/^premium/i.test(name || "");

// JackedRabbit staff accounts show up like any member, but the payout audit
// never pays out on them.
const INTERNAL_EMAIL = /@jackedrabbitgyms\.com$/i;

export const isTrial = (m: Member) => isTrialProduct(m.productName);

export const isActive = (m: Member) =>
  !isExpired(m.expiresAt || "") && !isMembershipCancelled(m.scheduledCancelAt || "");

export const daysLeft = (expiresAt?: string): number => {
  if (!expiresAt) return NaN;
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// The dashboard endpoint returns one row per paid billing period, so a member
// three months into an upgrade has three rows. Metrics about people group rows
// by email, which is unique per user.
interface Person {
  rows: Member[];
  active?: Member;
  firstStart?: string;
  firstTrialStart?: string;
  firstUpgradeStart?: string;
  checkedIn: boolean;
}

const earliest = (a: string | undefined, b: string | undefined) =>
  !a ? b : !b ? a : a < b ? a : b;

export const groupByPerson = (members: Member[]): Person[] => {
  const byKey = new Map<string, Person>();
  members.forEach((m) => {
    const key = (m.email || "").toLowerCase() || m.membershipId;
    const p = byKey.get(key) || { rows: [], checkedIn: false };
    p.rows.push(m);
    if (isActive(m)) p.active = m;
    if (m.isOnboarded) p.checkedIn = true;
    p.firstStart = earliest(p.firstStart, m.startedAt);
    if (isTrial(m)) p.firstTrialStart = earliest(p.firstTrialStart, m.startedAt);
    else p.firstUpgradeStart = earliest(p.firstUpgradeStart, m.startedAt);
    byKey.set(key, p);
  });
  return Array.from(byKey.values());
};

const hadTrial = (p: Person) => p.rows.some(isTrial);
const upgraded = (p: Person) => p.rows.some((r) => !isTrial(r));

export interface Kpis {
  activeMembers: number;
  trials: number;
  upgrades: number;
  totalMembers: number;
  conversionRate: number;
}

export const computeKpis = (members: Member[]): Kpis => {
  const people = groupByPerson(members);
  const active = people.filter((p) => p.active);
  const trialPeople = people.filter(hadTrial);
  const converted = trialPeople.filter(upgraded).length;
  return {
    activeMembers: active.length,
    trials: active.filter((p) => isTrial(p.active!)).length,
    upgrades: active.filter((p) => !isTrial(p.active!)).length,
    totalMembers: people.length,
    conversionRate:
      trialPeople.length > 0 ? Math.round((converted / trialPeople.length) * 100) : 0,
  };
};

export interface FunnelStage {
  key: "trials" | "checkedIn" | "upgraded" | "active";
  label: string;
  value: number;
}

// Each stage is a subset of the one before it, starting from everyone who
// bought a trial here.
export const computeFunnel = (members: Member[]): FunnelStage[] => {
  const trials = groupByPerson(members).filter(hadTrial);
  const checkedIn = trials.filter((p) => p.checkedIn);
  const upgradedPeople = checkedIn.filter(upgraded);
  const stillActive = upgradedPeople.filter((p) => p.active);
  return [
    { key: "trials", label: "Trial Purchases", value: trials.length },
    { key: "checkedIn", label: "Checked In", value: checkedIn.length },
    { key: "upgraded", label: "Upgraded", value: upgradedPeople.length },
    { key: "active", label: "Still Active", value: stillActive.length },
  ];
};

export const expiringSoon = (members: Member[], withinDays = 7): Member[] =>
  members
    .filter(isActive)
    .filter((m) => {
      const d = daysLeft(m.expiresAt);
      return !isNaN(d) && d >= 0 && d <= withinDays;
    })
    .sort((a, b) => daysLeft(a.expiresAt) - daysLeft(b.expiresAt));

export const upgradeOpportunities = (members: Member[]): Member[] =>
  members
    .filter(isActive)
    .filter(isTrial)
    .sort((a, b) => daysLeft(a.expiresAt) - daysLeft(b.expiresAt));

export interface TimePoint {
  month: string;
  label: string;
  value: number;
}

const monthKey = (iso: string) => iso.slice(0, 7);
const monthLabel = (key: string) => {
  const [y, m] = key.split("-");
  const names = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${names[Number(m) - 1]} '${y.slice(2)}`;
};

// Every month from the earliest key through the current month, so charts show
// quiet months as zero instead of drawing a line straight across them.
const monthRange = (keys: string[]): string[] => {
  if (keys.length === 0) return [];
  const sorted = [...keys].sort();
  const last = [sorted[sorted.length - 1], currentMonthKey()].sort()[1];
  let [y, m] = sorted[0].split("-").map(Number);
  const out: string[] = [];
  for (let key = sorted[0]; key <= last; ) {
    out.push(key);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
    key = `${y}-${String(m).padStart(2, "0")}`;
  }
  return out;
};

const countByMonth = (dates: (string | undefined)[]) => {
  const byMonth = new Map<string, number>();
  dates.forEach((d) => {
    if (!d) return;
    const k = monthKey(d);
    byMonth.set(k, (byMonth.get(k) || 0) + 1);
  });
  return byMonth;
};

export const growthSeries = (members: Member[]): TimePoint[] => {
  const byMonth = countByMonth(groupByPerson(members).map((p) => p.firstStart));
  let cumulative = 0;
  return Array.from(byMonth.keys())
    .sort()
    .map((k) => {
      cumulative += byMonth.get(k) || 0;
      return { month: k, label: monthLabel(k), value: cumulative };
    });
};

export const trialsSeries = (members: Member[]): TimePoint[] => {
  const byMonth = countByMonth(
    groupByPerson(members)
      .filter(hadTrial)
      .map((p) => p.firstTrialStart)
  );
  return monthRange(Array.from(byMonth.keys())).map((k) => ({
    month: k,
    label: monthLabel(k),
    value: byMonth.get(k) || 0,
  }));
};

// Lifetime trial-to-upgrade rate as of the end of each month, so the latest
// point matches the Conversion Rate KPI.
export const conversionTrend = (members: Member[]): TimePoint[] => {
  const trialPeople = groupByPerson(members).filter(hadTrial);
  const trialsByMonth = countByMonth(trialPeople.map((p) => p.firstTrialStart));
  const convByMonth = countByMonth(
    trialPeople.filter(upgraded).map((p) => p.firstUpgradeStart)
  );
  const months = Array.from(
    new Set(Array.from(trialsByMonth.keys()).concat(Array.from(convByMonth.keys())))
  ).sort();
  let trials = 0;
  let conv = 0;
  return months.map((k) => {
    trials += trialsByMonth.get(k) || 0;
    conv += convByMonth.get(k) || 0;
    const value = trials > 0 ? Math.round((conv / trials) * 100) : 0;
    return { month: k, label: monthLabel(k), value };
  });
};

export type MemberStatus = "trial" | "upgraded" | "cancelling" | "expired";

export interface MemberSummary {
  key: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string | null;
  status: MemberStatus;
  plan: string;
  firstJoined?: string;
  monthsPaid: number;
  expiresAt?: string;
  checkedIn: boolean;
  currentRow: Member;
}

const isCancelling = (m: Member) =>
  !!m.scheduledCancelAt && new Date(m.scheduledCancelAt).getTime() > Date.now();

export const memberDirectory = (members: Member[]): MemberSummary[] =>
  groupByPerson(members)
    .map((p) => {
      const latest = p.rows.reduce((a, b) => ((a.expiresAt || "") > (b.expiresAt || "") ? a : b));
      const current = p.active || latest;
      const status: MemberStatus = !p.active
        ? "expired"
        : isCancelling(p.active)
        ? "cancelling"
        : isTrial(p.active)
        ? "trial"
        : "upgraded";
      return {
        key: (current.email || current.membershipId).toLowerCase(),
        firstName: current.firstName,
        lastName: current.lastName,
        email: current.email,
        phone: current.phone,
        status,
        plan: planLabel(current.productName),
        firstJoined: p.firstStart,
        monthsPaid: p.rows.length,
        expiresAt: current.expiresAt,
        checkedIn: p.checkedIn,
        currentRow: current,
      };
    })
    .sort((a, b) => (b.expiresAt || "").localeCompare(a.expiresAt || ""));

const median = (values: number[]) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : Math.round((sorted[mid - 1] + sorted[mid]) / 2);
};

export interface FunnelInsights {
  checkInRate: number | null;
  upgradeRateAfterCheckIn: number | null;
  retentionRate: number | null;
  medianDaysToUpgrade: number | null;
  activeTrialsNotCheckedIn: Member[];
}

export const funnelInsights = (members: Member[]): FunnelInsights => {
  const [trials, checkedIn, upgradedStage, active] = computeFunnel(members).map((s) => s.value);
  const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : null);
  const days = groupByPerson(members)
    .filter((p) => p.firstTrialStart && p.firstUpgradeStart)
    .map((p) =>
      Math.round(
        (new Date(p.firstUpgradeStart!).getTime() - new Date(p.firstTrialStart!).getTime()) /
          (1000 * 60 * 60 * 24)
      )
    )
    .filter((d) => d >= 0);
  return {
    checkInRate: pct(checkedIn, trials),
    upgradeRateAfterCheckIn: pct(upgradedStage, checkedIn),
    retentionRate: pct(active, upgradedStage),
    medianDaysToUpgrade: median(days),
    activeTrialsNotCheckedIn: members.filter((m) => isActive(m) && isTrial(m) && !m.isOnboarded),
  };
};

export const isSetToCancel = isCancelling;

export interface Product {
  name?: string;
  price?: { preTaxCost?: number; total?: number };
  preTaxCost?: string | number;
}

export const productPrice = (p?: Product): number => {
  if (!p) return 0;
  return Number(p.price?.preTaxCost ?? p.preTaxCost ?? 0) || 0;
};

// Prefer the price of the membership's own product, as the payout audit does.
// Matching by plan name is the fallback for data loaded before the endpoint
// returned preTaxCost.
const priceForMember = (m: Member, products: Product[]): number => {
  if (m.preTaxCost !== undefined && m.preTaxCost !== null) return Number(m.preTaxCost) || 0;
  const name = (m.productName || "").toLowerCase();
  const match = products.find((p) => (p.name || "").toLowerCase() === name);
  return productPrice(match);
};

// Sums of fractional shares pick up float noise (135.20000000000002); snap to
// the nearest cent without dropping the cents.
const toCents = (dollars: number) => Math.round(dollars * 100) / 100;

// Share of each sale the studio keeps; JackedRabbit keeps the rest.
const STUDIO_SHARE_TRIAL = 0.5;
const STUDIO_SHARE_UPGRADE = 0.8;

export const studioShare = (price: number, productName?: string) =>
  price * (isTrialProduct(productName) ? STUDIO_SHARE_TRIAL : STUDIO_SHARE_UPGRADE);

// Payout rule: a billing period pays the studio its share of the full plan
// price in the UTC calendar month it was first checked in. The backend works
// out that month (payoutAt; periods paid under the old start-month rule keep
// their start month), so the dashboard and the payout report can't disagree.
// JackedRabbit staff accounts never pay out. Every earnings number on the
// dashboard goes through here.
export const isPaidOut = (m: Member) => !!m.isOnboarded && !INTERNAL_EMAIL.test(m.email || "");

const payoutMonth = (m: Member) => {
  const at = m.payoutAt || m.startedAt;
  return at ? monthKey(at) : undefined;
};

export const currentMonthKey = () => new Date().toISOString().slice(0, 7);

export interface MonthEarnings {
  trialEarnings: number;
  upgradeEarnings: number;
  totalEarnings: number;
  paidPeriods: number;
  awaitingCheckIn: { count: number; earnings: number };
}

export const monthEarnings = (
  members: Member[],
  products: Product[] = [],
  month: string = currentMonthKey()
): MonthEarnings => {
  const result: MonthEarnings = {
    trialEarnings: 0,
    upgradeEarnings: 0,
    totalEarnings: 0,
    paidPeriods: 0,
    awaitingCheckIn: { count: 0, earnings: 0 },
  };
  members.forEach((m) => {
    if (INTERNAL_EMAIL.test(m.email || "")) return;
    const share = studioShare(priceForMember(m, products), m.productName);
    if (isPaidOut(m)) {
      if (payoutMonth(m) !== month) return;
      result.paidPeriods += 1;
      if (isTrial(m)) result.trialEarnings += share;
      else result.upgradeEarnings += share;
      return;
    }
    // Not checked in yet: still pays this month if checked in before the period ends.
    if (!m.startedAt || monthKey(m.startedAt) > month || isExpired(m.expiresAt || "")) return;
    result.awaitingCheckIn.count += 1;
    result.awaitingCheckIn.earnings += share;
  });
  result.trialEarnings = toCents(result.trialEarnings);
  result.upgradeEarnings = toCents(result.upgradeEarnings);
  result.totalEarnings = toCents(result.trialEarnings + result.upgradeEarnings);
  result.awaitingCheckIn.earnings = toCents(result.awaitingCheckIn.earnings);
  return result;
};

export interface ProjectionResponse {
  windowDays: number;
  queuedInDatabase: number;
  verifiedWithStripe: number;
  products: { productName: string; preTaxCost: number; count: number }[];
  checkedAt: string;
}

export interface Projection {
  value: number;
  memberCount: number;
  windowDays: number;
}

export const projectionFromResponse = (res: ProjectionResponse): Projection => ({
  value: toCents(
    res.products.reduce((sum, p) => sum + studioShare(p.preTaxCost, p.productName) * p.count, 0)
  ),
  memberCount: res.verifiedWithStripe,
  windowDays: res.windowDays,
});

export const earningsSeries = (members: Member[], products: Product[] = []): TimePoint[] => {
  const byMonth = new Map<string, number>();
  members.filter(isPaidOut).forEach((m) => {
    const k = payoutMonth(m);
    if (!k) return;
    const earned = studioShare(priceForMember(m, products), m.productName);
    byMonth.set(k, (byMonth.get(k) || 0) + earned);
  });
  return monthRange(Array.from(byMonth.keys())).map((k) => ({
    month: k,
    label: monthLabel(k),
    value: toCents(byMonth.get(k) || 0),
  }));
};

export interface PlanBreakdown {
  productName: string;
  periods: number;
  price: number;
  studioEarnings: number;
}

export const earningsByPlan = (
  members: Member[],
  products: Product[] = [],
  month: string = currentMonthKey()
): PlanBreakdown[] => {
  const byPlan = new Map<string, PlanBreakdown>();
  members
    .filter((m) => isPaidOut(m) && payoutMonth(m) === month)
    .forEach((m) => {
      const productName = m.productName || "unknown";
      const price = priceForMember(m, products);
      const row = byPlan.get(productName) || { productName, periods: 0, price, studioEarnings: 0 };
      row.periods += 1;
      row.studioEarnings = toCents(row.studioEarnings + studioShare(price, productName));
      byPlan.set(productName, row);
    });
  return Array.from(byPlan.values()).sort((a, b) => b.studioEarnings - a.studioEarnings);
};

export const planLabel = (productName?: string) => {
  if (isTrialProduct(productName)) return "Trial";
  const name = (productName || "").toLowerCase();
  if (name.includes("yearly")) return "Upgrade · Yearly";
  if (name.includes("quarterly")) return "Upgrade · Quarterly";
  return "Upgrade · Monthly";
};

// Product prices (preTaxCost) are stored in dollars.
export const formatCurrency = (dollars: number): string => {
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
